import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { LocalActivity, Group, TrendingUp } from '@mui/icons-material';
import { brandColors } from '../../../../theme/theme';

/**
 * OpeningWeekendAnalysis Component
 * 
 * Dashboard combining Aware Opening Weekend and Outstanding Opening Weekend metrics
 * Shows total addressable audience for opening weekend and opportunity sizing
 */
const OpeningWeekendAnalysis = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="text.secondary">
          No data available for opening weekend analysis
        </Typography>
      </Paper>
    );
  }

  // Calculate overall totals
  const totals = data.reduce((acc, record) => ({
    totalResponses: acc.totalResponses + (record.TotalResponses || 0),
    totalAware: acc.totalAware + (record.TotalAware || 0),
    totalAwareOpeningWeekend: acc.totalAwareOpeningWeekend + (record.TotalAwareOpeningWeekend || 0),
    totalOutstandingOpeningWeekend: acc.totalOutstandingOpeningWeekend + (record.TotalOutstandingOpeningWeekend || 0)
  }), {
    totalResponses: 0,
    totalAware: 0,
    totalAwareOpeningWeekend: 0,
    totalOutstandingOpeningWeekend: 0
  });

  // Calculate percentages
  const awareOpeningPct = totals.totalAware > 0 ? (totals.totalAwareOpeningWeekend / totals.totalAware) * 100 : 0;
  const outstandingOpeningPct = totals.totalResponses > 0 ? (totals.totalOutstandingOpeningWeekend / totals.totalResponses) * 100 : 0;
  const totalOpeningWeekend = totals.totalAwareOpeningWeekend + totals.totalOutstandingOpeningWeekend;
  const totalOpeningPct = totals.totalResponses > 0 ? (totalOpeningWeekend / totals.totalResponses) * 100 : 0;

  // Break down by ethnicity
  const ethnicityBreakdown = {};
  data.forEach(record => {
    const ethnicity = record.ethnicity || 'Unknown';
    if (!ethnicityBreakdown[ethnicity]) {
      ethnicityBreakdown[ethnicity] = {
        ethnicity,
        totalAware: 0,
        awareOpening: 0,
        outstandingOpening: 0
      };
    }
    ethnicityBreakdown[ethnicity].totalAware += record.TotalAware || 0;
    ethnicityBreakdown[ethnicity].awareOpening += record.TotalAwareOpeningWeekend || 0;
    ethnicityBreakdown[ethnicity].outstandingOpening += record.TotalOutstandingOpeningWeekend || 0;
  });

  const ethnicityData = Object.values(ethnicityBreakdown).map(item => ({
    ...item,
    awareOpeningPct: item.totalAware > 0 ? (item.awareOpening / item.totalAware) * 100 : 0,
    total: item.awareOpening + item.outstandingOpening
  })).sort((a, b) => b.total - a.total);

  // Break down by age
  const ageBreakdown = {};
  data.forEach(record => {
    const age = record.agegroup || 'Unknown';
    if (!ageBreakdown[age]) {
      ageBreakdown[age] = {
        age,
        totalAware: 0,
        awareOpening: 0,
        outstandingOpening: 0
      };
    }
    ageBreakdown[age].totalAware += record.TotalAware || 0;
    ageBreakdown[age].awareOpening += record.TotalAwareOpeningWeekend || 0;
    ageBreakdown[age].outstandingOpening += record.TotalOutstandingOpeningWeekend || 0;
  });

  const ageData = Object.values(ageBreakdown).map(item => ({
    ...item,
    awareOpeningPct: item.totalAware > 0 ? (item.awareOpening / item.totalAware) * 100 : 0,
    total: item.awareOpening + item.outstandingOpening
  })).sort((a, b) => b.total - a.total);

  // Break down by gender
  const genderBreakdown = {};
  data.forEach(record => {
    const gender = record.gender || 'Unknown';
    if (!genderBreakdown[gender]) {
      genderBreakdown[gender] = {
        gender,
        totalAware: 0,
        awareOpening: 0,
        outstandingOpening: 0
      };
    }
    genderBreakdown[gender].totalAware += record.TotalAware || 0;
    genderBreakdown[gender].awareOpening += record.TotalAwareOpeningWeekend || 0;
    genderBreakdown[gender].outstandingOpening += record.TotalOutstandingOpeningWeekend || 0;
  });

  const genderData = Object.values(genderBreakdown).map(item => ({
    ...item,
    awareOpeningPct: item.totalAware > 0 ? (item.awareOpening / item.totalAware) * 100 : 0,
    total: item.awareOpening + item.outstandingOpening
  })).sort((a, b) => b.total - a.total);

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Opening Weekend Intent Analysis
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Analyze the total addressable audience planning to attend opening weekend
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: `2px solid ${brandColors.primary}40`, backgroundColor: `${brandColors.primary}05` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocalActivity sx={{ color: brandColors.primary, fontSize: 28, mr: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Aware - Opening Weekend
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: brandColors.primary, mb: 1 }}>
                {awareOpeningPct.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totals.totalAwareOpeningWeekend.toLocaleString()} of {totals.totalAware.toLocaleString()} aware
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: `2px solid ${brandColors.secondary}40`, backgroundColor: `${brandColors.secondary}05` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Group sx={{ color: brandColors.secondary, fontSize: 28, mr: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Outstanding - Opening Weekend
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: brandColors.secondary, mb: 1 }}>
                {outstandingOpeningPct.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totals.totalOutstandingOpeningWeekend.toLocaleString()} not yet aware but planning
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', border: `2px solid ${brandColors.success}40`, backgroundColor: `${brandColors.success}05` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUp sx={{ color: brandColors.success, fontSize: 28, mr: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Total Opening Weekend Intent
                </Typography>
              </Box>
              <Typography variant="h3" sx={{ fontWeight: 700, color: brandColors.success, mb: 1 }}>
                {totalOpeningPct.toFixed(1)}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totalOpeningWeekend.toLocaleString()} total respondents
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Demographic Breakdowns */}
      <Grid container spacing={3}>
        {/* By Ethnicity */}
        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              By Ethnicity
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ethnicityData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="ethnicity" type="category" width={90} />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'awareOpening') return [value, 'Aware Opening'];
                    if (name === 'outstandingOpening') return [value, 'Outstanding'];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar dataKey="awareOpening" stackId="a" fill={brandColors.primary} name="Aware Opening" />
                <Bar dataKey="outstandingOpening" stackId="a" fill={brandColors.secondary} name="Outstanding" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        {/* By Age */}
        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              By Age Group
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ageData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="age" type="category" width={90} />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'awareOpening') return [value, 'Aware Opening'];
                    if (name === 'outstandingOpening') return [value, 'Outstanding'];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar dataKey="awareOpening" stackId="a" fill={brandColors.primary} name="Aware Opening" />
                <Bar dataKey="outstandingOpening" stackId="a" fill={brandColors.secondary} name="Outstanding" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>

        {/* By Gender */}
        <Grid item xs={12} md={4}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              By Gender
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={genderData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="gender" type="category" width={90} />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'awareOpening') return [value, 'Aware Opening'];
                    if (name === 'outstandingOpening') return [value, 'Outstanding'];
                    return [value, name];
                  }}
                />
                <Legend />
                <Bar dataKey="awareOpening" stackId="a" fill={brandColors.primary} name="Aware Opening" />
                <Bar dataKey="outstandingOpening" stackId="a" fill={brandColors.secondary} name="Outstanding" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
      </Grid>

      {/* Opportunity Sizing */}
      <Box sx={{ mt: 3, p: 2, backgroundColor: `${brandColors.info}10`, borderRadius: 2, border: `1px solid ${brandColors.info}40` }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: brandColors.info }}>
          Opportunity Analysis
        </Typography>
        <Typography variant="body2">
          <strong>Expansion Potential:</strong> {totals.totalOutstandingOpeningWeekend.toLocaleString()} respondents who are not yet aware but plan to attend opening weekend represent a significant expansion opportunity. 
          If these audience members can be reached through targeted marketing, the opening weekend intent could increase from {awareOpeningPct.toFixed(1)}% to {totalOpeningPct.toFixed(1)}% 
          (a {((outstandingOpeningPct / awareOpeningPct) * 100).toFixed(0)}% increase).
        </Typography>
      </Box>
    </Paper>
  );
};

export default OpeningWeekendAnalysis;

